import { ApolloClient } from "@apollo/client";
import { composeProgress } from "apollo/utils";
import { GET_AUCTION_FORMS } from "core/apollo/graphql";
import {
  CLINICAL_CONSULTATION_ACTION,
  SEARCH_ACTION_DELETE_FORM,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFormName,
  Patient,
} from "core/types";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { useClinicalConsultationAction } from "dsl/hooks";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback } from "react";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

function writeGetAuctionFormsCacheFromMergedAuction(
  mutatedAuction: Auction | null,
  client: ApolloClient<object>,
) {
  const auctionId = mutatedAuction?.id;
  if (!auctionId) return;

  client.cache.updateQuery<{ auction: Auction | null }>(
    {
      query: GET_AUCTION_FORMS,
      variables: { auctionId },
    },
    (data) => ({
      auction: {
        ...data?.auction,
        ...mutatedAuction,
        __typename: "Auction",
        forms: mutatedAuction.forms ?? [],
        id: auctionId,
      },
    }),
  );
}

export function useDeleteConfirmation({
  auction,
  consultation,
  form,
  formName,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  formName: DocsFormName;
  form: AuctionForm | null | undefined;
  patient: Patient;
}) {
  const [deleteAuctionForm, deleteAuctionFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
  });

  const [deleteFormConsultation, deleteFormConsultationProgress] =
    useClinicalConsultationAction({
      actionType: CLINICAL_CONSULTATION_ACTION.DELETE_FORM,
    });

  // todo: remove when all files are synced with forms
  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction?.id ?? 0,
    consultationId: consultation?.id,
    patientId: patient.id,
    recareFileType: FORM_NAME_TO_FILE_TYPE[formName],
  });

  const onDelete = useCallback(async (): Promise<boolean> => {
    try {
      if (consultation) {
        if (!form?.id || !form.form_type) {
          console.error("form id or form type is not defined");
          return false;
        }
        const deleted = await deleteFormConsultation({
          consultation,
          context: {
            form_type: form.form_type,
            id: form.id,
          },
          patient,
        });
        if (!deleted) return false;
        deleteUnsharedFile();
        return true;
      }
      if (!auction) {
        console.error("auction is not defined");
        return false;
      }
      const deleted = await deleteAuctionForm({
        auction,
        context: {
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
        onCompleted: writeGetAuctionFormsCacheFromMergedAuction,
      });
      if (!deleted) return false;

      // Fire-and-forget: file sync is best-effort cleanup and should not block
      // the UI from resetting after form deletion. The todo above notes this
      // will be removed once all files are synced with forms.
      deleteUnsharedFile();
      return true;
    } catch (err) {
      console.error(`error deleting ${formName} form: ${getErrorMessage(err)}`);
      return false;
    }
  }, [
    auction,
    consultation,
    deleteAuctionForm,
    deleteFormConsultation,
    deleteUnsharedFile,
    form,
    formName,
    patient,
  ]);

  return {
    deleteFormProgress: composeProgress([
      deleteAuctionFormProgress,
      deleteFormConsultationProgress,
    ]),
    onDelete,
  };
}
